/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { ApiClient } from '@shared/api-client';
import { Tour, TourMonthStatistics } from '@shared/models';
import { DriverTeam } from '@shared/models/driver-team';
import { WorkflowAction } from '@shared/models/workflow-action';
import { forkJoin, Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { ToursState } from '@tours/store/tours.state';
import { getCurrentLocation } from '@tours/store/tours.selectors';

@Injectable({
  providedIn: 'root'
})
export class ToursService extends ApiClient {
  constructor(
    public httpClient: HttpClient,
    private datePipe: DatePipe,
    private toursState: Store<ToursState>,
  ) {
    super();
  }

  getToursForDay(date: Date): Observable<Tour[]> {
    const startDate = new Date(date);
    const endDate = new Date(date);
    endDate.setDate(date.getDate() + 1);

    return this.getTours(startDate, endDate);
  }

  getToursForConstructionSite(date: Date, constructionSiteId?: string): Observable<Tour[]> {
    const startDate = new Date(date);
    const endDate = new Date(date);
    endDate.setMonth(startDate.getMonth() + 1);

    return this.getToursForAllLocations(startDate, endDate, constructionSiteId);
  }

  getTours(startDate: Date, endDate: Date, constructionSiteId?: string): Observable<Tour[]> {
    const params = this.buildDateRangeParams(startDate, endDate, constructionSiteId);

    return this.addLocationToParams(params).pipe(
      switchMap((locationAwareParams) => {
        return this.httpClient.get<Tour[]>(`${environment.backendServer}/api/tours`, {
          headers: this.LD_JSON_HEADERS,
          params: locationAwareParams
        });
      }),
      map(response => response['hydra:member'])
    );
  }

  getToursForAllLocations(startDate: Date, endDate: Date, constructionSiteId?: string): Observable<Tour[]> {
    const params = this.buildDateRangeParams(startDate, endDate, constructionSiteId);

    return this.httpClient.get<Tour[]>(`${environment.backendServer}/api/tours/all-locations`, {
      headers: this.LD_JSON_HEADERS,
      params
    }).pipe(
      map(response => response['hydra:member'])
    );
  }

  private buildDateRangeParams(startDate: Date, endDate: Date, constructionSiteId?: string): any {
    const params: any = {
      'appointment.startDate[after]': this.datePipe.transform(startDate, 'yyyy-MM-dd'),
      'appointment.startDate[strictly_before]': this.datePipe.transform(endDate, 'yyyy-MM-dd')
    };

    if (constructionSiteId) {
      params.constructionSite = constructionSiteId;
    }

    return params;
  }

  getTour(id: string): Observable<Tour> {
    return this.httpClient.get<Tour>(`${environment.backendServer}/api/tours/${this.extractId(id)}`, {
      headers: this.LD_JSON_HEADERS
    });
  }

  createTour(tour: Tour): Observable<Tour> {
    // Build a new payload instead of mutating `tour`: it is an NgRx action
    // payload and is frozen by strictActionImmutability, so assigning to it
    // throws.
    const tourPayload: Tour = {
      ...tour,
      appointment: { ...tour.appointment, date: this.datePipe.transform(tour.appointment.date, 'yyyy-MM-dd') }
    };

    return this.toursState.select(getCurrentLocation).pipe(
      filter(location => location !== null),
      take(1),
      switchMap(location => {
        return this.httpClient.post<Tour>(`${environment.backendServer}/api/tours`, { ...tourPayload, location }, {
          headers: this.LD_JSON_HEADERS,
          observe: 'response'
        });
      }),
      map(response => response.body)
    );
  }

  updateTour(tour: Tour): Observable<Tour> {
    // Build a new payload instead of mutating `tour` (frozen NgRx action payload).
    const tourPayload: Tour = {
      ...tour,
      appointment: { ...tour.appointment, date: this.datePipe.transform(tour.appointment.date, 'yyyy-MM-dd') }
    };
    return this.httpClient.put<Tour>(`${environment.backendServer}/api/tours/${this.extractId(tour['@id'])}`, tourPayload, {
      headers: this.LD_JSON_HEADERS,
      observe: 'response'
    }).pipe(
      map(response => response.body)
    );
  }

  deleteTour(id: string): Observable<any> {
    return this.httpClient.delete(`${environment.backendServer}/api/tours/${this.extractId(id)}`, { observe: 'response' });
  }

  executeWorkflowAction(workflowAction: WorkflowAction): Observable<Tour> {
    return this.httpClient.request<Tour>(workflowAction.target.httpMethod, `${environment.backendServer}${workflowAction.target.url}`, {
      body: {transition: workflowAction.name},
      headers: this.LD_JSON_HEADERS
    });
  }

  postponeTours(tours: string[]): Observable<any> {
    const tourIds = tours.map(id => parseInt(this.extractId(id), 10));
    return this.httpClient.post(`${environment.backendServer}/api/tours/postpone`, { tours: tourIds }, {
      headers: this.LD_JSON_HEADERS,
      observe: 'response'
    });
  }

  getTourStatistics(month: Date): Observable<TourMonthStatistics> {
    const params: any = {
      year: this.datePipe.transform(month, 'yyyy'),
      month: this.datePipe.transform(month, 'M')
    };

    return this.addLocationToParams(params).pipe(
      switchMap(locationAwareParams => {
        return this.httpClient.get<TourMonthStatistics>(`${environment.backendServer}/api/tours/statistics`, {
          headers: this.LD_JSON_HEADERS,
          params: locationAwareParams,
        });
      })
    );
  }

  addLocationToParams(params: any): Observable<any> {
    return this.toursState.select(getCurrentLocation).pipe(
      filter(location => location !== null),
      take(1),
      map(location => {
        params.location = this.extractId(location['@id']);
        return params;
      })
    );
  }

  handleRemovedDriverTeams(desiredDriverTeams: DriverTeam[], actualDriverTeams: DriverTeam[]): Observable<any> {
    const desiredDriverTeamsIds = desiredDriverTeams.map((team) => team['@id']);
    const actualDriverTeamsIds = actualDriverTeams.map((team) => team['@id']);
    const driverTeamsToBeDeleted = actualDriverTeamsIds.filter(x => !desiredDriverTeamsIds.includes(x)).filter(x => x != null);
    return driverTeamsToBeDeleted.length > 0 ? forkJoin(driverTeamsToBeDeleted.map(teamId => this.deleteTeam(teamId))) : of({});
  }

  private deleteTeam(teamId: string): Observable<any> {
    return this.httpClient.delete(`${environment.backendServer}/api/teams/${this.extractId(teamId)}`);
  }
}
